/**
 * Each team's last results as W, L or T chips (success for a win, error for a
 * loss, neutral for a tie, the letter inside, design prompt 13), with the
 * opponent, score and date the server sent. ISSUE-988 WI-25.
 */
import { cn } from "@sportsmet/ui";
import type { ReactNode } from "react";

import type {
  typeFormResult,
  typeLastFive,
  typeMatchupTeamMark,
} from "./matchupTypes";
import { TeamMark } from "./TeamMark";
import { GameSectionCard } from "./GameSectionCard";

const CHIP_CLASSES: Record<typeFormResult["resultCode"], string> = {
  W: "bg-success-tint text-success",
  L: "bg-error-tint text-error",
  T: "bg-surface-sunken text-ink-secondary",
};

const FormColumn = ({
  team,
  results,
}: {
  team: typeMatchupTeamMark;
  results: readonly typeFormResult[];
}): ReactNode => (
  <div className="flex min-w-0 flex-col gap-2" data-testid="matchup-form-team">
    <div className="flex min-w-0 items-center gap-2">
      <TeamMark
        name={team.name}
        abbreviation={team.plateText}
        logoUrl={team.logoUrl}
        size="sm"
      />
      <h4 className="min-w-0 text-body-strong text-ink">{team.name}</h4>
    </div>
    {results.length === 0 ? (
      <span className="text-body text-ink-secondary">No results yet</span>
    ) : (
      results.map((result) => (
        <div
          key={result.key}
          data-testid="matchup-form-result"
          className="flex min-h-touch-target items-center gap-2 border-t border-hairline py-1"
        >
          <span
            aria-label={{ W: "Win", L: "Loss", T: "Tie" }[result.resultCode]}
            className={cn(
              "inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-sm text-caption font-bold",
              CHIP_CLASSES[result.resultCode],
            )}
          >
            {result.resultCode}
          </span>
          <span className="min-w-0 flex-1 text-body text-ink">
            {result.opponentLabel}
          </span>
          <span
            data-testid="matchup-form-date"
            className="w-24 shrink-0 text-small text-ink-secondary tabular-nums"
          >
            {result.dateLabel}
          </span>
          <span
            className="w-16 shrink-0 text-right text-body-strong text-ink tabular-nums"
            data-testid="matchup-form-score"
          >
            {result.scoreLabel}
          </span>
        </div>
      ))
    )}
  </div>
);

export const LastFiveSection = ({
  title,
  lastFive,
  away,
  home,
}: {
  title: string;
  lastFive: typeLastFive;
  away: typeMatchupTeamMark;
  home: typeMatchupTeamMark;
}): ReactNode => (
  <GameSectionCard
    testId="matchup-last-five"
    title={title}
    caption="Most recent first"
  >
    <div className="grid grid-cols-1 gap-4">
      <FormColumn team={away} results={lastFive.away} />
      <FormColumn team={home} results={lastFive.home} />
    </div>
  </GameSectionCard>
);
